import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { get, has, isEmpty } from 'lodash';
import { TrafficCone } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { StatusColorSwatch } from '@/components/inspector/status-color-swatch';
import { useBatchEditAssets } from '@/hooks/data/assets/useBatchEditAssets';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { type Asset, type AssetWorkflowMetadata } from '@/types/asset';
import { ToastHighlight } from '@/components/ui/toast-highlight';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { useWorkflowList } from '@/hooks/workflow/useWorkflow';
import { Label, LabelVariants } from '@/components/ui/label';
import { setWorkflow } from '@/services/workflow.service';
import { ObjectAction, ObjectType } from '@/types/batch';
import { type WorkflowStatuses } from '@/types/workflow';
import { useTreeContext } from '@/context/TreeContext';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { getUploadCsvKey } from '@/lib/utils';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const WorkflowAccordion = ({
  asset,
  multipleAssetsWorkflows,
  selectedItems,
  folderId,
  albumId,
  readOnly = false,
  enabled,
  isUpload,
}: {
  asset?: Asset;
  multipleAssetsWorkflows?: AssetWorkflowMetadata;
  selectedItems?: Array<{ id: string; name: string }>;
  folderId?: string;
  albumId?: string;
  disabled?: boolean;
  readOnly?: boolean;
  enabled: boolean;
  isUpload?: boolean;
}) => {
  const queryClient = useQueryClient();
  const tCommon = useTranslations('common');
  const tWorkflow = useTranslations('inspector.workflow');
  const tToasts = useTranslations('toasts');

  const [selectedWorkflow, setSelectedWorkflow] = useState<Record<string, any>>({});
  const [indeterminateWorkflows, setIndeterminateWorkflows] = useState<Record<string, boolean>>({});

  const { getActiveUploadSession, setManualMetadata } = useUploadStore();
  const { uploadSessionId, session } = getActiveUploadSession();
  const { selectedAssets: uploadSelectedAssetsIds, manualMetadata, csvData } = session;
  const { includeSubFolderAssets } = useTreeContext();
  const { batchEditAssetsMutation } = useBatchEditAssets();
  const { toast } = useToast();
  const { data: workflows, isFetching: workflowListIsFetching } = useWorkflowList(
    {
      pagination: {
        offset: null,
        limit: null,
      },
      sort: {
        value: SortOrderValues.CREATED_AT,
        order: SortOrderOptions.ASC,
      },
      queryString: '',
    },
    { enabled: enabled },
  );

  const uploadSelectedAssetIdsString = uploadSelectedAssetsIds?.map((a) => a.id).join(',');

  useEffect(() => {
    if (isUpload) {
      if (!uploadSelectedAssetsIds || uploadSelectedAssetsIds.length === 0) {
        setSelectedWorkflow({});
        setIndeterminateWorkflows({});
        return;
      }

      const allWorkflowsObjects: Array<Record<string, any>> = uploadSelectedAssetsIds.map(
        ({ id, name, originalName }) => {
          const csvKey = getUploadCsvKey({ name, originalName });
          const csvWorkflows = csvData[csvKey]?.workflows ?? {};
          const manualWorkflows = manualMetadata[id]?.workflows ?? {};
          return { ...csvWorkflows, ...manualWorkflows };
        },
      );

      const final: Record<string, any> = {};
      const indeterminate: Record<string, boolean> = {};
      const workflowKeys = new Set<string>();

      allWorkflowsObjects.forEach((workflowsObject) => {
        Object.keys(workflowsObject ?? {}).forEach((key) => {
          workflowKeys.add(key);
        });
      });

      workflowKeys.forEach((key) => {
        const values = allWorkflowsObjects.map((obj) => obj?.[key]);
        const definedValues = values.filter((value) => value !== undefined && value !== null);

        if (definedValues.length === 0) {
          return;
        }

        const firstValue = values[0];
        const isSameAcrossAll = values.every((value) => value === firstValue);

        if (isSameAcrossAll) {
          final[key] = firstValue;
        } else {
          indeterminate[key] = true;
        }
      });

      setSelectedWorkflow(final);
      setIndeterminateWorkflows(indeterminate);
      return;
    }

    setIndeterminateWorkflows({});
    if (multipleAssetsWorkflows) {
      setSelectedWorkflow(multipleAssetsWorkflows);
    } else if (asset?.workflows) {
      setSelectedWorkflow(asset?.workflows);
    }
  }, [
    asset?.workflows,
    csvData,
    isUpload,
    manualMetadata,
    multipleAssetsWorkflows,
    uploadSelectedAssetsIds,
    uploadSelectedAssetIdsString,
  ]);

  const setWorkflowWithAuth = useAuthenticatedQueryFn(setWorkflow);
  const setWorkflowMutation = useMutation({
    mutationFn: setWorkflowWithAuth,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKey.Asset] });

      toast({
        title: tToasts('setWorkflowMutation.title'),
        descriptionClassName: 'flex min-w-0 whitespace-pre',
        description: tToasts.rich('setWorkflowMutation.description', {
          assetName: asset?.name ?? '',
          highlight: (chunks) => <ToastHighlight chunks={chunks} />,
        }),
      });
    },
    onError: (err) => {
      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: err.message ?? tToasts('setWorkflowMutation.errorDescription'),
      });
    },
  });

  const handleChange = (selectedValue: string) => {
    const filteredWorkflow = workflows?.find((workflow) =>
      Object.prototype.hasOwnProperty.call(workflow.statuses, selectedValue),
    );

    if (!filteredWorkflow) {
      return;
    }

    const statusSlug = filteredWorkflow.slug;

    if (isUpload) {
      uploadSelectedAssetsIds.forEach((selectedAsset) => {
        const csvKey = getUploadCsvKey(selectedAsset);
        const csvWorkflow = (csvData[csvKey]?.workflows ?? {}) as Record<string, string>;

        const mergedWorkflow = {
          ...selectedWorkflow,
          [statusSlug]: selectedValue,
        };

        const manualOnlyWorkflow = Object.fromEntries(
          Object.entries(mergedWorkflow).filter(([key, value]) => csvWorkflow[key] !== value),
        );
        setManualMetadata(
          uploadSessionId,
          {
            workflows: manualOnlyWorkflow,
          },
          [
            {
              id: selectedAsset.id,
              name: selectedAsset.name,
              originalName: selectedAsset.originalName,
            },
          ],
          true,
        );
      });
    } else if (multipleAssetsWorkflows) {
      batchEditAssetsMutation.mutate({
        objectType: ObjectType.Asset,
        objectAction: ObjectAction.Update,
        filter: {
          ...(selectedItems && { assetIds: selectedItems.map((selectedAsset) => selectedAsset.id) }),
          ...(folderId && { folderId, includeSubFolderAssets }),
          ...(albumId && { albumId }),
        },
        payload: {
          workflows: { [statusSlug]: selectedValue },
        },
      });
    } else {
      setWorkflowMutation.mutate({
        asset_id: asset?.id,
        workflows: { [statusSlug]: selectedValue },
      });
    }
  };

  return (
    <div className="space-y-3 p-3">
      <Label variant={LabelVariants.INSPECTOR_SECTION}>{tWorkflow('label')}</Label>
      {workflows && workflows.length > 0 ? ( // TODO it should be with && workflows.statuses.length > 0 and the types should be adjusted
        workflows.map(
          ({ slug, statuses }) =>
            !isEmpty(statuses) && (
              <div key={slug} className="grid grid-cols-2 items-center gap-x-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="line-clamp-1 text-xs font-semibold wrap-anywhere capitalize">{slug}</span>
                  </TooltipTrigger>
                  <TooltipPortal>
                    <TooltipContent className="max-w-96">{slug}</TooltipContent>
                  </TooltipPortal>
                </Tooltip>
                <Select
                  key={isUpload ? uploadSessionId + uploadSelectedAssetIdsString + slug : `${slug}-${asset?.id}`}
                  onValueChange={handleChange}
                  disabled={workflowListIsFetching}
                  value={has(selectedWorkflow, slug) ? get(selectedWorkflow, slug) : ''}
                >
                  <SelectTrigger
                    disabled={readOnly}
                    className="bg-input-background"
                    indeterminate={
                      Boolean(multipleAssetsWorkflows && !multipleAssetsWorkflows[slug]) ||
                      (isUpload && uploadSelectedAssetsIds.length > 1 && Boolean(indeterminateWorkflows[slug]))
                    }
                  >
                    <SelectValue
                      placeholder={
                        <span className="line-clamp-1 text-[13px] wrap-anywhere text-text-gray-alternate">
                          {tCommon('selectAnOption')}
                        </span>
                      }
                    >
                      <div className="flex items-center">
                        {selectedWorkflow && has(selectedWorkflow, slug) && (
                          <StatusColorSwatch
                            color={(statuses as Record<string, WorkflowStatuses>)[get(selectedWorkflow, slug)].color}
                            className="mr-1.5"
                          />
                        )}
                        <span className="line-clamp-1 text-[13px] wrap-anywhere text-text-base">
                          {selectedWorkflow && has(selectedWorkflow, slug)
                            ? (statuses as Record<string, WorkflowStatuses>)[get(selectedWorkflow, slug)].name
                            : tCommon('selectAnOption')}
                        </span>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  {!readOnly && (
                    <SelectContent className="max-w-(--radix-select-trigger-width)">
                      {Object.entries(statuses as Record<string, WorkflowStatuses>).map(
                        ([status, { id, name, color }]) => (
                          <SelectItem key={id} value={status}>
                            <div className="flex items-center justify-between">
                              <StatusColorSwatch color={color} className="mr-2" />
                              <div className="min-w-0 wrap-anywhere">{name}</div>
                            </div>
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  )}
                </Select>
              </div>
            ),
        )
      ) : (
        <div className="py-4 text-center text-text-gray-alternate">
          <TrafficCone className="mx-auto mb-2" size={28} strokeWidth={1.5} />
          {tWorkflow('empty')}
        </div>
      )}
    </div>
  );
};
